import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Settings, Kanban, List, Plus, Zap, Check, ChartNoAxesGantt, PlayCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { FiPackage } from 'react-icons/fi';
import { format } from "date-fns";
import { getReleaseStatusColor } from "@/lib/colorsMap";
import { useState } from 'react';
import { ReleaseDetailsDialog } from "@/components/ReleaseDetailsDialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"

export const ProjectHeader = ({
    project,
    isLoading,
    selectedMembers,
    toggleMemberFilter,
    user,
    filterBy,
    sortBy,
    groupByAssignee,
    view,
    handleFilterChange,
    handleSortChange,
    handleGroupByChange,
    handleViewChange,
    handleOpenCreateTask,
    onShowReleaseDetails,
}) => {
    const navigate = useNavigate();
    const organizationSlug = project?.organizationSlug;
    const projectKey = project?.key;
    const [weekDropdownOpen, setWeekDropdownOpen] = useState(false);

    if (isLoading) {
        return (
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <div className="h-6 w-24 bg-muted animate-pulse rounded-md" />
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                    <div className="h-6 w-36 bg-muted animate-pulse rounded-md" />
                </div>

                <div className="flex justify-between items-center h-10">
                    {/* Project title and members skeleton */}
                    <div className="flex items-center space-x-4 animate-pulse">
                        <div className="h-7 w-48 bg-muted rounded-md" />
                        <div className="flex -space-x-2">
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="h-10 w-10 rounded-full bg-muted border-2 border-background relative before:absolute before:inset-0 before:rounded-full before:animate-pulse before:bg-muted"
                                />
                            ))}
                        </div>
                    </div>

                    {/* Project settings skeleton */}
                    <div className="flex items-center divide-x">
                        <div className="flex items-center space-x-2 pr-2">
                            <div className="h-8 w-24 bg-muted animate-pulse rounded-md" />
                        </div>
                        <div className="flex items-center px-2">
                            <div className="h-8 w-[67px] bg-muted animate-pulse rounded-md" />
                        </div>
                        <div className="pl-2">
                            <div className="h-8 w-[118px] bg-muted animate-pulse rounded-md" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const displayAndNewTask = () => {
        return (
            <>
                <div className="flex items-center space-x-2">
                <DropdownMenu>
                    <DropdownMenuTrigger
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 px-3 py-2 bg-background hover:bg-accent hover:text-accent-foreground border border-input transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                        aria-label="Display options"
                    >
                        <Settings className="h-4 w-4 mr-2 text-muted-foreground" />
                        Display
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[280px] p-3">
                        <DropdownMenuItem asChild className="md:hidden items-center mb-3 p-0">
                            <div className="flex items-center rounded-md border border-input h-8">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`h-full w-1/2 px-2 rounded-r-none border-r ${view === 'board' ? 'bg-secondary' : ''
                                        }`}
                                    onClick={() => handleViewChange('board')}
                                >
                                    <Kanban className="h-4 w-4" />
                                    <span className="ml-1.5">Board</span>
                                </Button>

                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`h-full w-1/2 px-2 rounded-l-none ${view === 'list' ? 'bg-secondary' : ''
                                        }`}
                                    onClick={() => handleViewChange('list')}
                                >
                                    <List className="h-4 w-4" />
                                    <span className="ml-1.5">List</span>
                                </Button>
                            </div>
                        </DropdownMenuItem>

                        <DropdownMenuSeparator className="md:hidden my-3" />

                        <div className="flex items-center justify-between mb-3">
                            <label className="text-xs text-muted-foreground w-28">Filter By</label>
                            <Select
                                value={selectedMembers.has(user.id) ? 'assigned' : filterBy}
                                onValueChange={handleFilterChange}
                                className="flex-1"
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select filter" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No filter</SelectItem>
                                    <SelectItem value="assigned">Assigned to me</SelectItem>
                                    <SelectItem value="created">Created by me</SelectItem>
                                    <SelectItem value="priority">High Priority</SelectItem>
                                    <SelectItem value="dueThisWeek">Due this week</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-muted-foreground w-28">Sort By</label>
                            <Select
                                value={sortBy}
                                onValueChange={handleSortChange}
                                className="flex-1"
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select sort" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="manual">Manual</SelectItem>
                                    <SelectItem value="created">Created date</SelectItem>
                                    <SelectItem value="updated">Last updated</SelectItem>
                                    <SelectItem value="due">Due date</SelectItem>
                                    <SelectItem value="priority">Priority</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <DropdownMenuSeparator className="my-3" />

                        <div className="space-y-3 mb-3">
                            <label className="text-xs text-muted-foreground">Board Options</label>
                            <div className="flex items-center justify-between py-1">
                                <div className="space-y-0.5">
                                    <div className="text-sm">Group by assignee</div>
                                    <div className="text-xs text-muted-foreground/50">Organize tasks by team member</div>
                                </div>
                                <Switch
                                    checked={groupByAssignee}
                                    onCheckedChange={handleGroupByChange}
                                />
                            </div>

                        </div>

                        <DropdownMenuSeparator className="my-3" />

                        <div className="-mx-2">
                            <Button
                                variant="ghost"
                                className="w-full justify-between h-auto py-1 px-2 hover:bg-accent"
                                onClick={() => navigate(`/${organizationSlug}/plan/${projectKey}/settings`)}
                            >
                                <div className="space-y-0.5 text-left">
                                    <div className="text-sm">All Project settings</div>
                                    <div className="text-xs text-muted-foreground/50">Configure project preferences</div>
                                </div>
                                <Settings className="h-4 w-4 shrink-0 ml-2" />
                            </Button>
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div >

                <TooltipProvider>
                    <div className="hidden md:flex items-center px-2">
                        <div className="flex items-center rounded-md border border-input h-8">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`h-full px-2 rounded-r-none border-r ${view === 'board' ? 'bg-secondary' : ''
                                            }`}
                                        onClick={() => handleViewChange('board')}
                                    >
                                        <Kanban className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Board view</p>
                                </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`h-full px-2 rounded-none border-r ${view === 'list' ? 'bg-secondary' : ''
                                            }`}
                                        onClick={() => handleViewChange('list')}
                                    >
                                        <List className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>List view</p>
                                </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`h-full px-2 rounded-l-none ${view === 'release' ? 'bg-secondary' : ''
                                            }`}
                                        onClick={() => handleViewChange('release')}
                                    >
                                        <ChartNoAxesGantt className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Release view</p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    </div>

                    <div className="md:pl-2">
                        <Button
                            size="sm"
                            className="h-8"
                            onClick={handleOpenCreateTask}
                        >
                            <Plus className="h-4 w-4" />
                            <span className="ml-1.5">New Task</span>
                        </Button>
                    </div>
                </TooltipProvider>
            </>
        )
    }

    return (
        <>
            <div className="space-y-2 md:space-y-4">
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-0">
                <div className="flex items-center justify-between w-full">
                    <Breadcrumb>
                        <BreadcrumbList className="text-sm md:text-base">
                            <BreadcrumbItem>
                                <BreadcrumbLink
                                    href={`/${organizationSlug}/plan`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/${organizationSlug}`);
                                    }}
                                    className="cursor-pointer"
                                >
                                    {project?.organizationName || organizationSlug}
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbLink
                                    href={`/${organizationSlug}/plan/${projectKey}/${view}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/${organizationSlug}/plan/${projectKey}/${view}`);
                                    }}
                                    className="cursor-pointer"
                                >
                                    {project?.name}
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    <div className="flex items-center gap-3">
                        {/* <TooltipProvider delayDuration={0}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        aria-label="Watch releases demo"
                                    className="h-7 px-3 bg-violet-50/60 hover:bg-violet-100/60 dark:bg-violet-950 dark:hover:bg-violet-900/50 shadow-sm"
                                        onClick={() => {
                                            const event = new CustomEvent('open-release-feature-video', { detail: { slide: 2 } });
                                            window.dispatchEvent(event);
                                        }}
                                    >
                                        <PlayCircle className="w-3.5 h-3.5 text-primary" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Watch releases demo</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider> */}

                        <DropdownMenu open={weekDropdownOpen} onOpenChange={setWeekDropdownOpen}>
                            <DropdownMenuTrigger asChild>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-7 px-2 bg-muted/60 hover:bg-muted/80 text-xs font-medium shadow-sm border border-muted/30"
                                >
                                    <Zap className="w-3 h-3 text-emerald-600 mr-1.5" />
                                    <span className="text-primary">
                                        {(() => {
                                            const { weekNumber, daysRemaining, hoursRemaining } = project.week;
                                            if (daysRemaining < 1) {
                                                return `Week ${weekNumber} • ${hoursRemaining} hour${hoursRemaining !== 1 ? 's' : ''} left`;
                                            }
                                            return `Week ${weekNumber} • ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left`;
                                        })()}
                                    </span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-auto min-w-[200px] max-w-[320px] mb-2">
                            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                                Active Releases
                            </div>
                            {project.releases && project.releases.filter(release => 
                                release.startDate && new Date(release.startDate) <= new Date()
                            ).length > 0 ? (
                                project.releases
                                    .filter(release => release.startDate && new Date(release.startDate) <= new Date())
                                    .map((release) => {
                                    const colors = getReleaseStatusColor(release.status);
                                    return (
                                        <DropdownMenuItem
                                            key={release.id}
                                            className="flex items-center gap-2 text-sm whitespace-nowrap pr-2"
                                            onClick={() => {
                                                onShowReleaseDetails && onShowReleaseDetails(release.id);
                                                setWeekDropdownOpen(false);
                                            }}
                                        >
                                            <FiPackage className={cn("w-3.5 h-3.5 flex-shrink-0", colors.icon)} />
                                            <span className="truncate">{release.name}</span>
                                            {release.targetDate && (
                                                <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
                                                    {format(new Date(release.targetDate), 'MMM d')}
                                                </span>
                                            )}
                                        </DropdownMenuItem>
                                    );
                                })
                            ) : (
                                <DropdownMenuItem disabled className="text-sm text-muted-foreground">
                                    No active releases
                                </DropdownMenuItem>
                            )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 md:gap-0">
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:space-x-4">
                    <h1 className="text-xl md:text-2xl font-semibold">{project?.name}</h1>
                    <div className="flex justify-between">
                        <div className="flex -space-x-2">
                        {project.members?.map((member) => (
                            <TooltipProvider key={member.id} delayDuration={0}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Avatar
                                            onClick={() => toggleMemberFilter(member.id)}
                                            className={`
                                                border-2
                                                relative
                                                ${selectedMembers.has(member.id)
                                                    ? 'border-background translate-y-[-2px] shadow-md z-10'
                                                    : 'border-background hover:translate-y-[-2px]'
                                                }
                                                transition-all
                                                cursor-pointer
                                                overflow-visible
                                            `}
                                        >
                                            <AvatarFallback
                                                className={`${member.avatarColor} text-white`}
                                            >
                                                {member.initials}
                                            </AvatarFallback>
                                            {selectedMembers.has(member.id) && (
                                                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-white rounded-full shadow-sm z-20 border border-2 border-background" />
                                            )}
                                        </Avatar>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{selectedMembers.has(member.id)
                                            ? `Remove ${member.name}'s filter`
                                            : `Filter by ${member.name}'s tasks`}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        ))}
                    </div>
                        <div className="flex items-center justify-end w-auto gap-2 md:divide-x md:hidden">
                            {displayAndNewTask()}
                        </div>
                    </div>

                </div>


                <div className="flex items-center justify-end w-auto gap-2 md:divide-x max-md:hidden">
                    {displayAndNewTask()}
                </div>
            </div>
        </div>


        </>
    );
};
